import React, { forwardRef } from 'react';
import Select, { components } from 'react-select';
import PropTypes from 'prop-types';
import { Icon, IconName } from '@usealloy/component-library';

import { getCombinedStyles, processCustomStyles, computeOptionData } from './BaseSelect';

const SingleSelectPropTypes = {
  /**
   * Change event handler
   */
  handleOnChange: PropTypes.func.isRequired,
  /**
   * Blur event handler
   */
  handleOnBlur: PropTypes.func,
  menuIsOpen: PropTypes.bool,
  /**
   * Array of possible options, each an object in the format
   * `{ label, value }`
   */
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
  /**
   * Which options are currently selected
   * Options can be specified by label or by index within the options list.
   */
  selectedOptions: PropTypes.oneOfType([
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ),
  ]),
  /** Whether this input is disabled (default false) */
  isDisabled: PropTypes.bool,
  /** Whether to support text search of the options (default false) */
  isSearchable: PropTypes.bool,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Extra styles. See docs: https://react-select.com/styles */
  customStyles: PropTypes.any,
  /** Whether the menu is initially open */
  isDefaultMenuOpen: PropTypes.bool,
  /** Whether the menu is initially focused */
  isAutoFocused: PropTypes.bool,
  /**
   * Custom components to override React-Select's default components
   * See https://react-select.com/components
   */
  customComponents: PropTypes.shape({}),
  callbackRef: PropTypes.oneOfType([ // ref to a native DOM element
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};
const SingleSelectDefaultPropTypes = {
  isDisabled: false,
  isSearchable: false,
  placeholder: null,
  customStyles: {},
  isDefaultMenuOpen: false,
  isAutoFocused: false,
  customComponents: {},
  handleOnBlur: null,
  selectedOptions: null,
  callbackRef: undefined,
};

const avgCharWidth = 8;
const getContainerWidth = longestOption => ((longestOption + avgCharWidth) * avgCharWidth);

const RefForwardedSelect = forwardRef((props, ref) => (
  <Select
    className="nodrag"
    {...props}
    ref={ref}
  />
));
RefForwardedSelect.displayName = 'RefForwardedSelect';

/**
 * Single-select component
 * Built on top of React Select - https://react-select.com/home
 *
 * @component
 * @deprecated Please use the Select component from the Component Library
 */
const SingleSelect = ({
  handleOnChange,
  handleOnBlur,
  options,
  selectedOptions = null,
  isDisabled,
  isSearchable,
  placeholder,
  customStyles,
  isDefaultMenuOpen = false,
  isAutoFocused = false,
  customComponents = {},
  callbackRef,
  menuIsOpen,
}) => {
  const {
    processedValues,
    longestOptionLength,
  } = computeOptionData(options, selectedOptions, false, placeholder);
  const singleSelectStyles = processCustomStyles(customStyles);
  singleSelectStyles.container = {
    width: getContainerWidth(longestOptionLength),
    ...customStyles?.container,
  };
  const styles = getCombinedStyles(singleSelectStyles);
  const value = processedValues.length !== 0 ? processedValues[0] : null;

  return (
    <RefForwardedSelect
      ref={callbackRef}
      styles={styles}
      value={value}
      onChange={handleOnChange}
      options={options}
      placeholder={placeholder}
      isSearchable={isSearchable}
      isMulti={false}
      isDisabled={isDisabled}
      defaultMenuIsOpen={isDefaultMenuOpen}
      components={{
        ...customComponents,
        DropdownIndicator: props => (
          <components.DropdownIndicator {...props}>
            <Icon icon={IconName.CaretDown} />
          </components.DropdownIndicator>
        ),
      }}
      onBlur={handleOnBlur}
      autoFocus={isAutoFocused}
      classNamePrefix="AlloySingleSelect"
      menuIsOpen={menuIsOpen}
    />
  );
};
SingleSelect.propTypes = SingleSelectPropTypes;
SingleSelect.defaultProps = SingleSelectDefaultPropTypes;

export default SingleSelect;
